import React, { useMemo } from 'react';
import { SimpleGrid, Stack } from '@mantine/core';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import KpiPage, {
  ChartCard,
  ChartTooltip,
  QueryState,
  StatTile,
  fmtInt,
  fmtMoney,
  fmtPct,
  useChartColors,
} from '../KpiPage';
import { kpiBySlug } from '../kpiCatalog';
import { useWindowParams } from '../DateWindowSelector';
import useNexusQuery, { field, num } from '../../../hooks/useNexusQuery';

const kpi = kpiBySlug('transaction-quality');

// Placeholder until Initiative 5 Work Action 1 sets the real figure. Passed to the query as an
// argument rather than hardcoded in SQL, so changing it here is the only edit needed.
const MARGIN_THRESHOLD = 20;

export default function TransactionQuality() {
  const c = useChartColors();
  const [window, setWindow] = useWindowParams();
  const args = useMemo(() => ({ ...window, threshold: String(MARGIN_THRESHOLD) }), [window]);

  const distribution = useNexusQuery('initiative-transaction-margin-distribution', args);
  const trend = useNexusQuery('initiative-transaction-quality-trend', args);

  const bands = useMemo(
    () =>
      distribution.rows.map((r) => ({
        band: field(r, 'Band'),
        sortOrder: num(r, 'SortOrder'),
        transactions: num(r, 'Transactions'),
        revenue: num(r, 'Revenue'),
        margin: num(r, 'GrossMargin'),
      })),
    [distribution.rows]
  );

  const trendData = useMemo(
    () =>
      trend.rows.map((r) => ({
        month: field(r, 'MonthKey'),
        transactions: num(r, 'Transactions'),
        marginPct: num(r, 'MarginPct'),
        belowThreshold: num(r, 'BelowThreshold'),
        belowThresholdPct: num(r, 'BelowThresholdPct'),
        negative: num(r, 'NegativeMargin'),
        negativeDollars: num(r, 'NegativeMarginDollars'),
      })),
    [trend.rows]
  );

  const summary = useMemo(() => {
    const total = bands.reduce((s, b) => s + b.transactions, 0);
    const negBand = bands.find((b) => b.band === 'Negative');
    const belowThreshold = bands
      .filter((b) => b.sortOrder <= 3) // Negative, 0-10%, 10-20% — all under a 20% threshold
      .reduce((s, b) => s + b.transactions, 0);
    const negDollars = trendData.reduce((s, d) => s + d.negativeDollars, 0);
    return {
      total,
      negative: negBand?.transactions ?? 0,
      negativeDollars: negDollars,
      belowThreshold,
      belowThresholdPct: total ? (belowThreshold / total) * 100 : 0,
    };
  }, [bands, trendData]);

  const bandColor = (b) => {
    if (b.band === 'Negative') return c.negative;
    if (b.sortOrder <= 3) return c.series[3];
    return c.positive;
  };

  return (
    <KpiPage kpi={kpi} window={window} onWindowChange={setWindow} hideMethodology>
      <Stack gap="md">
        <QueryState
          isLoading={distribution.isLoading}
          error={distribution.error}
          isEmpty={!bands.length}
        >
          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
            <StatTile
              label="Transactions"
              value={fmtInt(summary.total)}
              sub="Invoice documents in window"
            />
            <StatTile
              label="Negative margin"
              value={fmtInt(summary.negative)}
              sub={`${fmtMoney(summary.negativeDollars)} of margin lost`}
              color={c.negative}
            />
            <StatTile
              label={`Below ${MARGIN_THRESHOLD}% margin`}
              value={fmtPct(summary.belowThresholdPct)}
              sub={`${fmtInt(summary.belowThreshold)} transactions`}
              color={c.series[3]}
            />
            <StatTile
              label="Threshold in use"
              value={`${MARGIN_THRESHOLD}%`}
              sub="Placeholder — Work Action 1 sets the real one"
            />
          </SimpleGrid>
        </QueryState>

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <ChartCard
            title="Transactions by margin band"
            caption="The shape of deal quality. Averages hide the tail — this is where the monthly review's outlier bucket comes from, rather than nominating deals from memory."
          >
            <QueryState
              isLoading={distribution.isLoading}
              error={distribution.error}
              isEmpty={!bands.length}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bands} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
                  <XAxis dataKey="band" stroke={c.axis} fontSize={11} interval={0} />
                  <YAxis stroke={c.axis} fontSize={12} width={52} />
                  <Tooltip
                    content={<ChartTooltip formatter={(v) => `${fmtInt(v)} transactions`} />}
                    cursor={{ fill: c.grid, opacity: 0.3 }}
                    wrapperStyle={{ outline: 'none' }}
                  />
                  <Bar dataKey="transactions" name="Transactions" radius={[4, 4, 0, 0]}>
                    {bands.map((b) => (
                      <Cell key={b.band} fill={bandColor(b)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </QueryState>
          </ChartCard>

          <ChartCard
            title="Deal quality over time"
            caption="Below-threshold share against overall margin rate. If the review program works, the below-threshold line should fall without the margin line simply riding a mix change."
          >
            <QueryState
              isLoading={trend.isLoading}
              error={trend.error}
              isEmpty={!trendData.length}
            >
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trendData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
                  <XAxis dataKey="month" stroke={c.axis} fontSize={10} />
                  <YAxis
                    yAxisId="left"
                    stroke={c.axis}
                    fontSize={12}
                    tickFormatter={(v) => `${Math.round(v)}%`}
                    width={48}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    stroke={c.axis}
                    fontSize={12}
                    width={40}
                  />
                  <Tooltip
                    content={
                      <ChartTooltip
                        formatter={(v, e) =>
                          e.dataKey === 'negative' ? `${fmtInt(v)} deals` : fmtPct(v)
                        }
                      />
                    }
                    cursor={{ fill: c.grid, opacity: 0.3 }}
                    wrapperStyle={{ outline: 'none' }}
                  />
                  <Legend iconType="circle" />
                  <Bar
                    yAxisId="left"
                    dataKey="belowThresholdPct"
                    name={`Below ${MARGIN_THRESHOLD}%`}
                    fill={c.series[3]}
                    radius={[4, 4, 0, 0]}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="marginPct"
                    name="Overall margin rate"
                    stroke={c.series[0]}
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="negative"
                    name="Negative-margin deals"
                    stroke={c.negative}
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </QueryState>
          </ChartCard>
        </SimpleGrid>
      </Stack>
    </KpiPage>
  );
}
